import * as React from "react";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Chip, Stack, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreRounded";
import type { BasePrompt, PromptGroup } from "../../models/domain";

const preSx = {
  m: 0,
  p: 1.5,
  maxHeight: 360,
  overflow: "auto",
  borderRadius: 1.5,
  bgcolor: "action.hover",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 12,
  lineHeight: 1.5,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
} as const;

const flatSx = { "&:before": { display: "none" }, boxShadow: "none", border: 1, borderColor: "divider" } as const;

/** Un prompt, replié : son nom et sa version, le texte à la demande. */
const PromptItem: React.FC<{ prompt: BasePrompt }> = ({ prompt }) => (
  <Accordion disableGutters TransitionProps={{ unmountOnExit: true }} sx={flatSx}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {prompt.label ?? prompt.id}
        </Typography>
        {prompt.version && <Chip size="small" variant="outlined" label={prompt.version} />}
      </Stack>
    </AccordionSummary>
    <AccordionDetails>
      <Box component="pre" sx={preSx}>
        {prompt.text}
      </Box>
    </AccordionDetails>
  </Accordion>
);

interface Props {
  version?: string;
  /** L'ordre d'assemblage du message, quand il ne se lit pas dans la liste. */
  assembly?: string[];
  /** Une seule section sans libellé = les prompts à plat (faits). */
  groups: PromptGroup[];
  /** Le bloc qui précède la consigne ; absent pour un rôle sans consigne. */
  instructionsHeader?: string;
  /** Ce que reçoit le bloc de consigne, dit en clair. */
  instructionsNote?: string;
  /** Prompts indisponibles (service injoignable, ou d'avant cette route). */
  error?: string | null;
}

/**
 * Le prompt de base d'un agent, en LECTURE SEULE et replié : ce que la consigne
 * complète. Les textes viennent du service qui les envoie (tc_embed_be pour les
 * faits, tc_learn pour les quêtes), emplacements `{…}` visibles.
 */
export const BasePromptPanel: React.FC<Props> = ({ version, assembly, groups, instructionsHeader, instructionsNote, error }) => (
  <Accordion disableGutters TransitionProps={{ unmountOnExit: true }} sx={flatSx}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2">Prompt de base</Typography>
        {version && <Chip size="small" label={version} />}
        <Typography variant="caption" color="text.secondary">
          lecture seule
        </Typography>
      </Stack>
    </AccordionSummary>
    <AccordionDetails>
      {error ? (
        <Alert severity="warning">{error}</Alert>
      ) : (
        <Stack spacing={1.25}>
          {assembly && assembly.length > 0 && (
            <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
              {assembly.map((step) => (
                <Typography component="li" variant="caption" color="text.secondary" key={step}>
                  {step}
                </Typography>
              ))}
            </Box>
          )}
          {groups.length === 1 && !groups[0].label
            ? groups[0].prompts.map((p) => <PromptItem key={p.id} prompt={p} />)
            : groups.map((g) => (
                <Accordion key={g.id} disableGutters TransitionProps={{ unmountOnExit: true }} sx={flatSx}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2" sx={{ fontWeight: 650 }}>
                      {g.form ? `${g.form} — ${g.label}` : g.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1, alignSelf: "center" }}>
                      {g.prompts.length} texte{g.prompts.length > 1 ? "s" : ""}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={0.75}>
                      {g.prompts.map((p) => (
                        <PromptItem key={p.id} prompt={p} />
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
          {instructionsHeader !== undefined && (
            <Box>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                Puis, en fin de message, si une consigne s'applique : {instructionsNote}
              </Typography>
              <Box component="pre" sx={preSx}>
                {`${instructionsHeader.trimStart()}<consigne>`}
              </Box>
            </Box>
          )}
        </Stack>
      )}
    </AccordionDetails>
  </Accordion>
);

export default BasePromptPanel;
